import { useState } from 'react';
import { useDispatch } from 'react-redux';
import * as adminHandlers from '../../../../routes/Admin/redux/handlers';

const OrderCard = ({ order, onView }) => {
    const dispatch = useDispatch();
    const [showCancelConfirm, setShowCancelConfirm] = useState(false);
    const [showRefundOptions, setShowRefundOptions] = useState(false);
    const [refundType, setRefundType] = useState(null);
    const formatCurrency = (value) => {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        }).format(value || 0);
    };

    const formatDate = (dateString) => {
        let adjustedDateString = dateString;
        if (!dateString.includes('Z') && !dateString.includes('+') && !dateString.includes('T')) {
            adjustedDateString = dateString + 'Z';
        } else if (dateString.includes('T') && !dateString.includes('Z') && !dateString.includes('+')) {
            adjustedDateString = dateString + 'Z';
        }
        
        const date = new Date(adjustedDateString);
        
        return date.toLocaleDateString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            timeZone: 'Asia/Kolkata'
        }).toUpperCase().replace(/ /g, ', ');
    };

    const formatTime = (dateString) => {
        let adjustedDateString = dateString;
        if (!dateString.includes('Z') && !dateString.includes('+') && !dateString.includes('T')) {
            adjustedDateString = dateString + 'Z';
        } else if (dateString.includes('T') && !dateString.includes('Z') && !dateString.includes('+')) {
            adjustedDateString = dateString + 'Z';
        }
        
        const date = new Date(adjustedDateString);
        
        return date.toLocaleTimeString('en-US', {
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hour12: true,
            timeZone: 'Asia/Kolkata'
        }).toUpperCase();
    };

    const getStatusClass = (status) => {
        switch(status) {
            case 'SUCCESS':
                return 'bg-green-500 text-black';
            case 'INITIATED':
            case 'VALIDATION_PENDING':
            case 'PAYMENT_PENDING':
            case 'GENERATION_PENDING':
            case 'REFUND_PENDING':
                return 'bg-yellow-500 text-black';
            case 'VALIDATION_FAILED':
            case 'PAYMENT_FAILED':
            case 'GENERATION_FAILED':
            case 'REFUND_FAILED':
                return 'bg-red-500 text-white';
            case 'CANCELLED':
            case 'REFUND_EXHAUSTED':
                return 'bg-gray-500 text-white';
            case 'SOURCE_REFUNDED':
            case 'WALLET_REFUNDED':
                return 'bg-blue-500 text-white';
            default:
                return 'bg-gray-500 text-white';
        }
    };

    // Extract data from the actual API structure
    const subBrand = order.sub_brand || {};
    const offer = order.offer || {};
    const user = order.user || {};
    
    const card = order.cards?.[0] || {};
    const calculation = order.calculations?.[0] || {};
    const vendorDetail = order.vendor_details?.[0] || {};

    const handleCancelOrder = async () => {
        try {
            await dispatch(adminHandlers.cancelOrderHandler(order.id));
            setShowCancelConfirm(false);
            // Refresh orders list if needed
        } catch (error) {
            console.error('Failed to cancel order:', error);
        }
    };

    const handleRefundOrder = async (type) => {
        try {
            await dispatch(adminHandlers.refundOrderHandler(order.id, type));
            setShowRefundOptions(false);
            setRefundType(null);
            // Refresh orders list if needed
        } catch (error) {
            console.error('Failed to refund order:', error);
        }
    };

    return (
        <>
        <div className="bg-eerie-black border border-zinc-700 rounded-2xl">
            {/* Top Section */}
            <div className="flex items-start justify-between border-b border-zinc-700 px-6 py-6">
                {/* Brand Info */}
                <div className="flex items-start flex-col gap-4">
                    <div className="flex items-center gap-4">
                        <div className="h-16 w-16 rounded-full">
                            {subBrand.image ? (
                                <img src={subBrand.image} alt={subBrand.name} className="h-16 w-16 rounded-full object-cover" />
                            ) : (
                                <div className="h-16 w-16 bg-gradient-to-br from-primary to-primary-dark rounded-full flex items-center justify-center">
                                    <span className="text-white font-bold text-xl">
                                        {subBrand.name ? subBrand.name[0].toUpperCase() : 'Z'}
                                    </span>
                                </div>
                            )}
                        </div>
                        <div>
                            <h3 className="text-white font-medium text-lg">{subBrand.name}</h3>
                            <p className="text-xs text-zinc-500">ORDER ID: {order.id}</p>
                        </div>
                    </div>
                    <div className="flex items-start gap-20">
                        <p className="text-sm"><span className="font-medium text-zinc-500">EXT REF NO:</span> {order.external_reference_number}</p>
                        <p className="text-sm"><span className="font-medium text-zinc-500">EXT ORDER NO:</span> {order.external_order_id || 'N/A'}</p>
                    </div>
                </div>

                {/* Right Side Info */}
                <div className="flex items-start gap-16">
                    {/* Coupon */}
                    <div>
                        <p className="text-xs text-zinc-500 mb-2">Coupon</p>
                        <p className="text-white font-medium">{offer?.code || 'N/A'}</p>
                    </div>

                    {/* Vendor Name */}
                    <div>
                        <p className="text-xs text-zinc-500 mb-2">Vendor Name</p>
                        <p className="text-white">{vendorDetail.wallet?.vendor || 'N/A'}</p>
                    </div>

                    {/* Vendor Balance */}
                    <div>
                        <p className="text-xs text-zinc-500 mb-2">SVC Balance</p>
                        <p className="text-white font-bold">{formatCurrency(vendorDetail.wallet?.balance || 0)}</p>
                    </div>

                    {/* Current State */}
                    <div>
                        <p className="text-xs text-zinc-500 mb-2 text-right">Current Status</p>
                        <button 
                            className={`px-3 py-1.5 rounded-lg text-xs font-medium uppercase ${getStatusClass(order.current_state)}`}
                        >
                            {order.current_state}
                        </button>
                    </div>
                </div>
            </div>

            {/* Customer Info Row */}
            <div className=" border-zinc-700 border-b px-6 py-6">
               
                <div className="flex items-center gap-12">
                    <div>
                        <p className="text-xs text-zinc-500 mb-1">Name</p>
                        <p className="text-white text-sm font-medium">{user?.first_name} {user?.last_name}</p>
                    </div>
                    <div>
                        <p className="text-xs text-zinc-500 mb-1">Mobile Number</p>
                        <p className="text-white text-sm">{user?.mobile}</p>
                    </div>
                    <div>
                        <p className="text-xs text-zinc-500 mb-1">Email</p>
                        <p className="text-white text-sm flex items-center gap-2">
                            {user?.email || 'N/A'}
                        </p>
                    </div>
                    <div>
                        <p className="text-xs text-zinc-500 mb-1">Order Date</p>
                        <p className="text-white text-sm">{formatDate(order.created_at)} : {formatTime(order.created_at)}</p>
                    </div>
                </div>
            </div>

            {/* Bottom Section */}
            <div className="flex items-center justify-between px-6 py-6">
                {/* Discount Info */}
                <div className="flex items-center gap-8">
                    <div>
                        <p className="text-xs text-zinc-500 mb-1">Brand Discount</p>
                        <div className="flex items-baseline gap-1">
                            <span className="text-white text-sm">{formatCurrency(calculation.sub_brand_discount_amount)}</span>
                            <span className="text-white text-sm font-medium">
                                ({calculation.sub_brand_discount_amount > 0 ? `${Math.round((calculation.sub_brand_discount_amount / calculation.total_amount) * 100)}%` : '10%'})
                            </span>
                        </div>
                    </div>
                    <div>
                        <p className="text-xs text-zinc-500 mb-1">Offer Discount</p>
                        <div className="flex items-baseline gap-1">
                            <span className="text-white text-sm">{formatCurrency(calculation.offer_discount_amount)}</span>
                            <span className="text-white text-sm font-medium">
                                ({calculation.offer_discount_amount > 0 ? `${Math.round((calculation.offer_discount_amount / calculation.total_amount) * 100)}%` : '8%'})
                            </span>
                        </div>
                    </div>
                    <div>
                        <p className="text-xs text-zinc-500 mb-1">Cashback</p>
                        <div className="flex items-baseline gap-1">
                            <span className="text-white text-sm">{formatCurrency(calculation.sub_brand_cashback_amount)}</span>
                            <span className="text-white text-sm font-medium">
                                ({calculation.sub_brand_cashback_amount > 0 ? `${Math.round((calculation.sub_brand_cashback_amount / calculation.total_amount) * 100)}%` : '2%'})
                            </span>
                        </div>
                    </div>
                    <div>
                        <p className="text-xs text-zinc-500 mb-1">Additional Balance</p>
                        <p className="text-white text-sm">{formatCurrency(calculation.additional_balance_amount)}</p>
                    </div>
                    <div>
                        <p className="text-xs text-zinc-500 mb-1">Settled Balance</p>
                        <p className="text-white text-sm">{formatCurrency(calculation.settled_balance_amount)}</p>
                    </div>
                    <div>
                        <p className="text-xs text-zinc-500 mb-1">Payable Balance</p>
                        <p className="text-white text-sm">{formatCurrency(calculation.payable_balance_amount)}</p>
                    </div>
                    <div>
                        <p className="text-xs text-zinc-500 mb-1">Total Amount</p>
                        <p className="text-white text-sm font-bold">{formatCurrency(calculation.total_amount)}</p>
                    </div>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-2 items-center">
                    {order.current_state === 'CANCELLED' ? (
                        <>
                            <button 
                                onClick={() => {
                                    setRefundType('source');
                                    setShowRefundOptions(true);
                                }}
                                className="px-3 py-1.5 bg-blue-600 text-white rounded-lg text-xs hover:bg-blue-700 transition-colors"
                            >
                                Refund to Source
                            </button>
                            <button 
                                onClick={() => {
                                    setRefundType('wallet');
                                    setShowRefundOptions(true);
                                }}
                                className="px-3 py-1.5 bg-green-600 text-white rounded-lg text-xs hover:bg-green-700 transition-colors"
                            >
                                Refund to Wallet
                            </button>
                        </>
                    ) : (order.current_state === 'SUCCESS' || order.current_state === 'GENERATION_FAILED') ? (
                        <button 
                            onClick={() => setShowCancelConfirm(true)}
                            className="px-3 py-1.5 bg-red-600 text-white rounded-lg text-xs hover:bg-red-700 transition-colors"
                        >
                            Cancel Order
                        </button>
                    ) : (
                        <></>
                    )}
                </div>
            </div>
        </div>

        {/* Cancel Confirmation Modal */}
        {showCancelConfirm && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div className="bg-charleston-green border border-zinc-700 rounded-lg p-6 max-w-sm w-full mx-4">
                    <h3 className="text-white text-lg font-medium mb-3">Confirm Cancellation</h3>
                    <p className="text-zinc-400 text-sm mb-6">
                        Are you sure you want to cancel this order? This action cannot be undone.
                    </p>
                    <div className="flex gap-3">
                        <button
                            onClick={() => setShowCancelConfirm(false)}
                            className="flex-1 px-4 py-2 bg-zinc-700 text-white rounded-lg text-sm hover:bg-zinc-600 transition-colors"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleCancelOrder}
                            className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg text-sm hover:bg-red-700 transition-colors"
                        >
                            Confirm Cancel
                        </button>
                    </div>
                </div>
            </div>
        )}

        {/* Refund Confirmation Modal */}
        {showRefundOptions && refundType && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div className="bg-charleston-green border border-zinc-700 rounded-lg p-6 max-w-sm w-full mx-4">
                    <h3 className="text-white text-lg font-medium mb-3">Confirm Refund</h3>
                    <p className="text-zinc-400 text-sm mb-6">
                        Are you sure you want to refund this order to {refundType === 'source' ? 'original payment source' : 'user wallet'}?
                    </p>
                    <div className="flex gap-3">
                        <button
                            onClick={() => {
                                setShowRefundOptions(false);
                                setRefundType(null);
                            }}
                            className="flex-1 px-4 py-2 bg-zinc-700 text-white rounded-lg text-sm hover:bg-zinc-600 transition-colors"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={() => handleRefundOrder(refundType)}
                            className={`flex-1 px-4 py-2 text-white rounded-lg text-sm transition-colors ${
                                refundType === 'source' 
                                    ? 'bg-blue-600 hover:bg-blue-700' 
                                    : 'bg-green-600 hover:bg-green-700'
                            }`}
                        >
                            Confirm Refund
                        </button>
                    </div>
                </div>
            </div>
        )}
        </>
    );
};

export default OrderCard;